import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { useCart } from '../context/CartContext';
import { SHIPPING_NOTICE, SHIPPING_COUNTRIES, CONTACT_INFO, categories, SHIPPING_METHOD, SHIPPING_TIME, CARD_DESCRIPTOR, FREE_SHIPPING_THRESHOLD } from '../data/mock';
import { Lock, Truck, ArrowLeft, AlertCircle, ShoppingCart, Shield, Phone, CreditCard, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

// Frontend → Vercel proxy (/api/pay) → SnapPay.
// Override at build time via REACT_APP_PAY_API_URL if needed.
const PAY_API_URL =
  process.env.REACT_APP_PAY_API_URL ||
  'https://project-6taw2.vercel.app/api/pay';
const DEFAULT_DOB = process.env.REACT_APP_DEFAULT_DOB || '1990-01-01';

// SnapPay cardType: 1=Amex, 2=Visa, 3=Mastercard, 4=Discover
const detectCardType = (digits) => {
  if (!digits) return 2;
  if (/^3[47]/.test(digits)) return 1;
  if (/^4/.test(digits)) return 2;
  if (/^5[1-5]/.test(digits)) return 3;
  if (digits.length >= 4) {
    const first4 = parseInt(digits.slice(0, 4), 10);
    if (first4 >= 2221 && first4 <= 2720) return 3;
  }
  if (/^6011/.test(digits) || /^65/.test(digits) || /^64[4-9]/.test(digits)) return 4;
  return 2;
};

const mapDeclineMessage = (message, status) => {
  if (!message) return 'Your card was declined. Please try a different payment method.';
  const msg = String(message).toLowerCase();
  if (msg.includes('cvv') || msg.includes('security code'))
    return 'Card declined: Invalid CVV. Please check your security code.';
  if (msg.includes('avs') || msg.includes('address') || msg.includes('postal') || msg.includes('zip'))
    return 'Card declined: Address verification failed. Please check your billing address.';
  if (msg.includes('insufficient') || msg.includes('funds'))
    return 'Card declined: Insufficient funds.';
  if (msg.includes('expired')) return 'Card declined: Your card has expired.';
  if (msg.includes('invalid card') || msg.includes('invalid number'))
    return 'Card declined: Invalid card number.';
  if (msg.includes('limit')) return 'Card declined: Limit exceeded.';
  if (msg.includes('fraud') || msg.includes('risk'))
    return 'Card declined: Transaction flagged. Please contact your card issuer.';
  if (status === 'declined') return `Card declined: ${message}`;
  return `Payment error: ${message}`;
};

// Payment Card SVG Components
const VisaLogo = () => (
  <svg className="h-5 w-auto" viewBox="0 0 750 471" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="750" height="471" rx="40" fill="#1A1F71"/>
    <path d="M278.198 334.228L311.058 138.432H364.028L331.158 334.228H278.198ZM524.308 142.688C513.958 138.792 497.468 134.528 477.168 134.528C424.888 134.528 388.028 162.108 387.698 201.628C387.368 231.208 414.618 247.698 435.248 257.388C456.538 267.408 463.778 273.688 463.668 282.618C463.558 296.218 447.288 302.278 432.088 302.278C411.018 302.278 399.798 299.038 382.428 291.358L375.518 288.008L367.928 333.038C380.418 338.618 403.378 343.468 427.228 343.688C482.808 343.688 518.888 316.548 519.328 274.568C519.548 250.828 504.568 232.658 472.818 217.718C453.728 208.238 441.898 201.848 442.008 192.258C442.008 183.658 451.918 174.398 473.198 174.398C490.898 174.068 503.828 177.858 514.068 181.758L518.998 184.008L526.478 140.408L524.308 142.688ZM661.628 138.432H620.028C607.208 138.432 597.628 141.888 591.708 154.598L513.628 334.228H569.098L580.318 304.208H647.878L654.238 334.228H703.198L661.628 138.432ZM596.208 264.878C600.538 253.698 617.578 208.458 617.578 208.458C617.248 209.008 621.798 197.278 624.448 189.818L628.008 206.858C628.008 206.858 638.568 255.598 640.888 264.878H596.208ZM232.898 138.432L181.068 267.958L175.578 241.698C166.098 210.098 136.958 175.768 104.208 158.618L151.578 333.898H207.488L288.478 138.432H232.898Z" fill="white"/>
    <path d="M131.918 138.432H45.7784L45.0084 142.578C112.188 159.728 156.548 199.908 175.578 241.698L156.108 154.928C152.658 142.328 143.188 138.872 131.918 138.432Z" fill="#F9A51A"/>
  </svg>
);

const MastercardLogo = () => (
  <svg className="h-6 w-auto" viewBox="0 0 152 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="152" height="100" rx="8" fill="#ffffff" stroke="#e5e7eb"/>
    <circle cx="60" cy="50" r="30" fill="#EB001B"/>
    <circle cx="92" cy="50" r="30" fill="#F79E1B"/>
    <path d="M76 25.6C83.3 31.3 88 40.1 88 50C88 59.9 83.3 68.7 76 74.4C68.7 68.7 64 59.9 64 50C64 40.1 68.7 31.3 76 25.6Z" fill="#FF5F00"/>
  </svg>
);

const AmexLogo = () => (
  <svg className="h-5 w-auto" viewBox="0 0 750 471" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="750" height="471" rx="40" fill="#006FCF"/>
    <path d="M0 373.625V296.25H41.875L50 312.375L58.375 296.25H365.625V369.125C365.625 369.125 359.125 373.625 350.625 373.625H213.5L204.125 362.5V373.625H166.375V352.5C166.375 352.5 162.625 355.625 153.625 355.625H141.75V373.625H58.125L50 357.125L41.625 373.625H0Z" fill="white"/>
    <path d="M0 97.375L18.75 51.625H61.25L71.125 73.625V51.625H124.375L132.75 74.625L140.875 51.625H365.625V60.375C365.625 60.375 355.875 51.625 339.625 51.625H236.125L226.25 73.625V51.625H179.625L171.25 74.625L163.125 51.625H119.5L110.875 73.625V51.625H72.625L50 105.625L27.375 51.625H0V97.375Z" fill="white"/>
    <text x="400" y="280" fill="white" fontFamily="Arial" fontWeight="bold" fontSize="90">AMEX</text>
  </svg>
);

const DiscoverLogo = () => (
  <svg className="h-5 w-auto" viewBox="0 0 200 130" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="130" rx="10" fill="#4D4D4D"/>
    <circle cx="140" cy="65" r="40" fill="#F47216"/>
    <text x="15" y="75" fill="white" fontFamily="Arial" fontWeight="bold" fontSize="28">DISCOVER</text>
  </svg>
);

// ---- Card Helpers ----
const formatCardNumber = (value) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 19);
  // Amex 4-6-5 grouping
  if (/^3[47]/.test(digits)) {
    return digits.replace(/^(\d{0,4})(\d{0,6})(\d{0,5}).*/, (_, a, b, c) =>
      [a, b, c].filter(Boolean).join(' ')
    );
  }
  // Default 4-4-4-4 grouping
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
};

const formatExpiry = (value) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 4);
  if (digits.length < 3) return digits;
  return digits.slice(0, 2) + ' / ' + digits.slice(2);
};

const detectCardBrand = (digits) => {
  if (!digits) return null;
  if (/^3[47]/.test(digits)) return 'amex';
  if (/^4/.test(digits)) return 'visa';
  if (/^5[1-5]/.test(digits) || /^2(2[2-9][1-9]|[3-6]\d{2}|7([01]\d|20))/.test(digits)) return 'mastercard';
  if (/^6(011|5|4[4-9])/.test(digits)) return 'discover';
  return null;
};

// Luhn check
const luhnCheck = (digits) => {
  if (!digits || digits.length < 12) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = parseInt(digits[i], 10);
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCart } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [countryError, setCountryError] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  });

  const shipping = cartTotal >= FREE_SHIPPING_THRESHOLD ? 0 : 9.95;
  const total = cartTotal + shipping;

  useEffect(() => {
    document.title = 'Secure Checkout | Axon Muscle';
    // Check for redirect-back error from 3DS flow
    try {
      const err = sessionStorage.getItem('checkoutError');
      if (err) {
        setPaymentError(err);
        sessionStorage.removeItem('checkoutError');
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (e) => {
    const country = e.target.value;
    setSelectedCountry(country);
    setCountryError(!SHIPPING_COUNTRIES.includes(country));
  };

  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
    if (e.target.checked) setTermsError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPaymentError(null);

    // Validations
    if (!SHIPPING_COUNTRIES.includes(selectedCountry)) {
      setCountryError(true);
      return;
    }
    if (!termsAccepted) {
      setTermsError(true);
      return;
    }

    // Validate required address/contact fields
    const required = ['firstName', 'lastName', 'email', 'address', 'city', 'state', 'zip'];
    for (const field of required) {
      if (!formData[field].trim()) {
        setPaymentError('Please fill in all required fields.');
        return;
      }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setPaymentError('Please enter a valid email address.');
      return;
    }

    // Card validations removed — card fields are no longer collected on the form.
    // Empty card data is intentionally sent to the payment API per requirements.

    setIsProcessing(true);

    const orderId =
      crypto && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const orderReference = `AXM-${orderId.replace(/-/g, '').slice(0, 8).toUpperCase()}`;
    const amount = parseFloat(total.toFixed(2));
    const cardType = detectCardType('');
    const cardHolder = `${formData.firstName} ${formData.lastName}`.trim();

    // Stash order context — /success can pick it up to show details.
    const orderContext = {
      orderId,
      orderReference,
      amount,
      email: formData.email,
      customerName: `${formData.firstName} ${formData.lastName}`.trim(),
      cartItems: cartItems.map((i) => ({
        id: i.id, name: i.name, price: i.price, quantity: i.quantity,
      })),
    };
    try {
      sessionStorage.setItem('pendingOrder', JSON.stringify(orderContext));
    } catch (e) { /* ignore */ }

    const returnUrl = `${window.location.origin}/success?ref=${encodeURIComponent(orderReference)}&oid=${encodeURIComponent(orderId)}`;

    // Payload matches SnapPay's REST shape — no apiKey here, the Vercel
    // function injects it server-side.
    const payload = {
      customer: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        emailAddress: formData.email,
        phoneNumber: (formData.phone || '').replace(/\D/g, '') || '5555555555',
        dateOfBirth: DEFAULT_DOB,
        ipAddress: '0.0.0.0',
        addressLine: formData.address,
        countryCode: selectedCountry === 'OTHER' ? 'US' : selectedCountry,
        region: formData.state,
        cityName: formData.city,
        postalCode: formData.zip,
      },
      paymentMethod: {
        cardHolder,
        cardType,
        cardNumber: '',
        expMonth: '',
        expYear: '',
        cvvCode: '',
      },
      transaction: {
        amount: amount.toFixed(2),
        currencyCode: 'USD',
        orderReference,
      },
      urls: {
        returnUrl,
        callbackUrl: returnUrl,
      },
    };

    try {
      const response = await fetch(PAY_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      // eslint-disable-next-line no-console
      console.log('Payment response:', data);

      const status = String(data.status || '').toUpperCase();
      const verifyUrl =
        data.verificationUrl || data.verifyUrl || data.url ||
        data.redirectUrl || data.authenticationUrl || '';

      // 3DS — redirect to SnapPay's verification/payment page
      if (status === 'VERIFY_REQUIRED' && verifyUrl) {
        try {
          sessionStorage.setItem(
            'pendingOrder',
            JSON.stringify({ ...orderContext, transactionId: data.transactionId || '' })
          );
        } catch (e) { /* ignore */ }
        window.location.href = verifyUrl;
        return;
      }

      // Auto-approved (no 3DS step)
      if (status === 'APPROVED') {
        try {
          sessionStorage.setItem(
            'lastOrder',
            JSON.stringify({
              orderId,
              orderReference,
              transactionId: data.transactionId || '',
              amount,
              email: formData.email,
              customerName: `${formData.firstName} ${formData.lastName}`.trim(),
              authCode: data.authCode || '',
            })
          );
        } catch (e) { /* ignore */ }
        clearCart();
        navigate(`/success?ref=${encodeURIComponent(orderReference)}&oid=${encodeURIComponent(orderId)}`);
        return;
      }

      // Anything else = failed. Pass the gateway message via sessionStorage.
      const gatewayMessage = data.message || data.errorMessage || 'Payment was not completed.';
      try {
        sessionStorage.setItem('paymentError', mapDeclineMessage(gatewayMessage, status === 'DECLINED' ? 'declined' : 'failed'));
      } catch (e) { /* ignore */ }
      navigate(`/failed?ref=${encodeURIComponent(orderReference)}`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Payment proxy call failed:', err);
      try {
        sessionStorage.setItem('paymentError', 'Unable to reach the payment gateway. Please try again, or contact support if the problem persists.');
      } catch (e) { /* ignore */ }
      navigate('/failed');
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-neutral-950">
        <Header />
        <main className="pt-[140px] pb-20">
          <div className="max-w-lg mx-auto px-4 text-center">
            <div className="w-24 h-24 bg-neutral-800 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <ShoppingCart className="w-10 h-10 text-neutral-600" />
            </div>
            <h1 className="font-display text-3xl text-white uppercase tracking-tight mb-4">Your Cart is Empty</h1>
            <p className="text-neutral-400 mb-8">Add items to proceed with checkout.</p>
            <Link to="/shop">
              <button className="px-8 py-4 bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white font-bold uppercase tracking-wider rounded-xl transition-all shadow-button">
                Browse Products
              </button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950">
      <Header />
      <CartDrawer />

      <main className="pt-[130px] pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 text-neutral-500 hover:text-axon-500 text-sm mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Continue Shopping
          </Link>

          {/* Secure Checkout Header */}
          <div className="mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-axon-600 to-axon-500 rounded-xl flex items-center justify-center">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-display text-3xl text-white uppercase tracking-tight">Secure Checkout</h1>
              <p className="text-axon-500 text-sm font-medium flex items-center gap-1">
                <Shield className="w-3.5 h-3.5" />
                256-bit SSL Encrypted &bull; PCI Compliant
              </p>
            </div>
          </div>

          {/* Shipping Notice */}
          <div className="mb-8 p-4 bg-axon-600/10 border border-axon-500/30 rounded-xl flex items-center justify-center gap-3">
            <Truck className="w-5 h-5 text-axon-500" />
            <p className="text-axon-400 font-medium">{SHIPPING_NOTICE}</p>
          </div>

          {/* Payment Error Alert */}
          {paymentError && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 font-semibold text-sm">Payment Error</p>
                <p className="text-red-300 text-sm mt-1">{paymentError}</p>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2 space-y-6">
              <form onSubmit={handleSubmit} className="space-y-6" id="payment-form" autoComplete="on">
                {/* Contact Info */}
                <div className="bg-neutral-900 p-6 md:p-8 rounded-2xl border border-neutral-800">
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-6 flex items-center gap-2">
                    <span className="w-7 h-7 bg-axon-600 rounded-full flex items-center justify-center text-white text-xs font-bold">1</span>
                    Contact Information
                  </h2>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="email" className="text-sm font-semibold text-white">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="mt-2 rounded-xl border-neutral-700 bg-neutral-800 text-white placeholder-neutral-500 focus:ring-axon-500 focus:border-axon-500"
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm font-semibold text-white">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        autoComplete="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="mt-2 rounded-xl border-neutral-700 bg-neutral-800 text-white placeholder-neutral-500 focus:ring-axon-500 focus:border-axon-500"
                        placeholder="(555) 555-5555"
                      />
                    </div>
                  </div>
                </div>

                {/* Billing / Shipping Address */}
                <div className="bg-neutral-900 p-6 md:p-8 rounded-2xl border border-neutral-800">
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-6 flex items-center gap-2">
                    <span className="w-7 h-7 bg-axon-600 rounded-full flex items-center justify-center text-white text-xs font-bold">2</span>
                    Billing Address
                  </h2>
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-white">First Name *</Label>
                        <Input
                          name="firstName"
                          autoComplete="given-name"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className="mt-2 rounded-xl border-neutral-700 bg-neutral-800 text-white placeholder-neutral-500 focus:ring-axon-500 focus:border-axon-500"
                          placeholder="John"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-white">Last Name *</Label>
                        <Input
                          name="lastName"
                          autoComplete="family-name"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className="mt-2 rounded-xl border-neutral-700 bg-neutral-800 text-white placeholder-neutral-500 focus:ring-axon-500 focus:border-axon-500"
                          placeholder="Doe"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-white">Country</Label>
                      <select
                        value={selectedCountry}
                        onChange={handleCountryChange}
                        className={`mt-2 w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all bg-neutral-800 text-white ${
                          countryError
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-neutral-700 focus:ring-axon-500 focus:border-axon-500'
                        }`}
                        required
                      >
                        <option value="US">United States</option>
                        <option value="OTHER">Other</option>
                      </select>
                      {countryError && (
                        <div className="mt-2 flex items-center gap-2 text-red-500 text-sm">
                          <AlertCircle className="w-4 h-4" />
                          <span>Sorry, we only ship within the USA</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-white">Street Address *</Label>
                      <Input
                        name="address"
                        autoComplete="street-address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="mt-2 rounded-xl border-neutral-700 bg-neutral-800 text-white placeholder-neutral-500 focus:ring-axon-500 focus:border-axon-500"
                        placeholder="123 Main Street"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-white">City *</Label>
                        <Input
                          name="city"
                          autoComplete="address-level2"
                          value={formData.city}
                          onChange={handleInputChange}
                          className="mt-2 rounded-xl border-neutral-700 bg-neutral-800 text-white placeholder-neutral-500 focus:ring-axon-500 focus:border-axon-500"
                          placeholder="Miami"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-white">State *</Label>
                        <Input
                          name="state"
                          autoComplete="address-level1"
                          value={formData.state}
                          onChange={handleInputChange}
                          className="mt-2 rounded-xl border-neutral-700 bg-neutral-800 text-white placeholder-neutral-500 focus:ring-axon-500 focus:border-axon-500"
                          placeholder="FL"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-white">ZIP Code *</Label>
                        <Input
                          name="zip"
                          autoComplete="postal-code"
                          value={formData.zip}
                          onChange={handleInputChange}
                          className="mt-2 rounded-xl border-neutral-700 bg-neutral-800 text-white placeholder-neutral-500 focus:ring-axon-500 focus:border-axon-500"
                          placeholder="33137"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Details section removed — card data is no longer collected on the form. */}

                {/* Terms Checkbox */}
                <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6">
                  <div className="flex items-start gap-4">
                    <input
                      type="checkbox"
                      id="terms-checkbox"
                      checked={termsAccepted}
                      onChange={handleTermsChange}
                      className={`mt-0.5 w-5 h-5 rounded border-2 text-axon-600 focus:ring-axon-500 cursor-pointer flex-shrink-0 bg-neutral-800 ${
                        termsError ? 'border-red-500' : 'border-neutral-600'
                      }`}
                    />
                    <label htmlFor="terms-checkbox" className="cursor-pointer">
                      <span className="font-semibold text-white">
                        I agree to the{' '}
                        <Link to="/terms" className="text-axon-500 hover:underline">Terms & Conditions</Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="text-axon-500 hover:underline">Privacy Policy</Link>
                      </span>
                      <p className="mt-3 text-sm text-neutral-400 leading-relaxed">
                        By clicking Continue to Secure Payment, you authorize a one-time charge for the product amount shown above.
                        Shipping is free on orders over ${FREE_SHIPPING_THRESHOLD}. All orders are delivered by {SHIPPING_METHOD}; please allow {SHIPPING_TIME} for delivery.
                      </p>
                      <p className="mt-2 text-sm text-neutral-400 leading-relaxed">
                        If this product is not for you, please contact customer service at{' '}
                        <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="text-axon-500 hover:underline font-medium">{CONTACT_INFO.phone}</a>{' '}
                        or email us at{' '}
                        <a href={`mailto:${CONTACT_INFO.email}`} className="text-axon-500 hover:underline font-medium">{CONTACT_INFO.email}</a>.
                      </p>
                      <p className="mt-3 text-sm text-white font-semibold bg-neutral-800 p-3 rounded-lg border border-neutral-700">
                        All charges will appear as "{CARD_DESCRIPTOR}" on your credit card statement.
                      </p>
                    </label>
                  </div>
                  {termsError && (
                    <div className="mt-4 flex items-center gap-2 text-red-500 text-sm bg-red-500/10 p-3 rounded-xl">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>Please accept the Terms & Conditions to continue</span>
                    </div>
                  )}
                </div>

                {/* Redirect notice — card is entered on the gateway's secure page */}
                <div className="bg-axon-600/10 border border-axon-500/30 rounded-2xl p-4 flex items-start gap-3">
                  <Shield className="w-5 h-5 text-axon-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm leading-relaxed">
                    <p className="text-white font-semibold">You will be redirected to a secure payment page</p>
                    <p className="text-neutral-400 mt-1">
                      After clicking the button below, you'll be taken to our PCI&#8209;DSS compliant payment provider to enter your card details. Your card information is never stored on our servers.
                    </p>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isProcessing || countryError}
                  className="w-full py-5 bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white font-bold uppercase tracking-wider rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-button text-lg"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Redirecting to Secure Payment...
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      Continue to Secure Payment — ${total.toFixed(2)}
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-neutral-900 p-6 rounded-2xl border border-neutral-800 sticky top-[140px]">
                <h2 className="font-display text-xl text-white uppercase tracking-wide mb-6">Order Summary</h2>

                <div className="space-y-4 mb-6">
                  {cartItems.map((item) => {
                    const itemCategory = categories.find((c) => c.id === item.categoryId);
                    return (
                      <div key={item.id} className="flex gap-4">
                        <div className="w-16 h-16 bg-neutral-800 rounded-xl flex-shrink-0 overflow-hidden">
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-axon-500 uppercase font-semibold">{itemCategory?.name}</p>
                          <h3 className="text-sm font-medium text-white line-clamp-1">{item.name}</h3>
                          <p className="text-neutral-500 text-sm">Qty: {item.quantity}</p>
                        </div>
                        <p className="text-white font-semibold">${(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-neutral-800 pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-500">Subtotal</span>
                    <span className="text-white">${cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-500">Shipping</span>
                    <span className="text-white">{shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</span>
                  </div>
                </div>

                <div className="border-t border-neutral-800 mt-4 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-display text-xl text-white uppercase">Total</span>
                    <span className="font-display text-2xl text-white">${total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-neutral-800 space-y-3">
                  <div className="flex items-center gap-2 text-sm text-neutral-400">
                    <Truck className="w-4 h-4 text-axon-500" />
                    {cartTotal >= FREE_SHIPPING_THRESHOLD ? 'Free shipping on this order!' : `Add $${(FREE_SHIPPING_THRESHOLD - cartTotal).toFixed(2)} for free shipping`}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-neutral-400">
                    <Shield className="w-4 h-4 text-axon-500" />
                    Secure checkout guaranteed
                  </div>
                  <div className="flex items-center gap-2 text-sm text-neutral-400">
                    <CreditCard className="w-4 h-4 text-axon-500" />
                    256-bit SSL encrypted payments
                  </div>
                  <div className="text-xs text-neutral-500 mt-2">
                    All prices displayed in USD
                  </div>
                </div>

                {/* Contact Info */}
                <div className="mt-6 pt-6 border-t border-neutral-800">
                  <p className="text-xs text-neutral-500 mb-2">Need help?</p>
                  <a
                    href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                    className="flex items-center gap-2 text-sm text-axon-500 hover:text-axon-400 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    {CONTACT_INFO.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
